import { Button, IconButton } from "@hortis/ui/button";
import { Dialog, DialogTrigger } from "@hortis/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import {
  DotsHorizontal,
  ExternalLink1,
  Eye,
  EyeOff,
  QrCode1,
  QrCode2,
} from "@hortis/ui/icons";
import { Link, useParams } from "@tanstack/react-router";
import {
  MaterialFieldsFragment,
  MaterialGroup,
  PlantMaterialStatus,
} from "generated/graphql";
import { type ReactNode, useState } from "react";
import { PublicQRCodeDialogContent } from "src/components/dialog/variants/plant-qr-code";
import { MakePrivateDialog } from "src/features/material-page/header/make-private-dialog";
import { MakePublicDialog } from "src/features/material-page/header/make-public-dialog";
import { useCollectionSiteSettings } from "src/utils/hooks/collection-site-from-url";
import { twMerge } from "tailwind-merge";

export const PublicRecordBanner = ({
  material,
  className,
  compact = false,
  children,
}: {
  material: MaterialFieldsFragment;
  className?: string;
  compact?: boolean;
  children?: ReactNode;
}) => {
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [makePublicDialogOpen, setMakePublicDialogOpen] = useState(false);
  const [makePrivateDialogOpen, setMakePrivateDialogOpen] = useState(false);
  const isPublic = material.public;
  const { isPublic: isSitePublic } = useCollectionSiteSettings();
  const isDesktop = useMediaQuery("lg");
  const { siteSlug } = useParams({
    from: "/_layout/site/$siteSlug",
  });
  const isPresentPlant =
    material.status === PlantMaterialStatus.Present &&
    material.materialGroup === MaterialGroup.Plant &&
    !material.archived;

  const fullSize = isDesktop && !compact;

  return isPublic && isPresentPlant && isSitePublic ? (
    <div
      className={twMerge(
        "bg-primary-50 font-medium text-sm text-primary-700 py-1 px-6 flex items-center gap-4 border-b border-primary-100 border-dotted animate-in fade-in slide-in-from-top-2 relative",
        fullSize ? "justify-center" : "justify-between",
        className,
      )}
      data-cy="public-record-banner"
    >
      <div className="flex items-center gap-2">
        <Eye className="text-primary-600 w-4 h-4" />
        {isDesktop ? "Record is public" : "Public"}
      </div>
      {children}
      {fullSize ? (
        <div className="flex items-center gap-1">
          <Button
            variant="secondaryColor"
            size="xs"
            className="hover:bg-primary-100 px-2 py-1.5 border border-primary-100"
            startIcon={ExternalLink1}
            asChild
            data-cy="view-public-record-button"
          >
            <a
              target="_blank"
              href={`/public/site/${siteSlug}/map/plant/${material.id}`}
            >
              View
            </a>
          </Button>
          {material.accession?.taxon != null && (
            <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="secondaryColor"
                  size="xs"
                  className="hover:bg-primary-100 py-1.5 px-2 border border-primary-100"
                  startIcon={QrCode2}
                  data-cy="view-qr-code-button"
                >
                  QR Code
                </Button>
              </DialogTrigger>
              <PublicQRCodeDialogContent
                materialId={material.id}
                scientificName={material.accession.taxon.scientificName}
                accessionNumber={material.accession.accessionNumber}
                qualifier={material.qualifier}
              />
            </Dialog>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                ariaLabel="Actions"
                variant="secondaryColor"
                size="xs"
                className="hover:bg-primary-100 p-1.5 border border-primary-100"
                icon={DotsHorizontal}
                data-cy="public-record-actions-button"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {isPublic && (
                <DropdownMenuItem
                  data-cy="make-private-menu-item"
                  onClick={() => {
                    setMakePrivateDialogOpen(true);
                  }}
                >
                  <EyeOff className="h-4 w-4 text-grey-500" />
                  Make private
                </DropdownMenuItem>
              )}
              {!isPublic && (
                <DropdownMenuItem
                  data-cy="make-public-menu-item"
                  onClick={() => {
                    setMakePublicDialogOpen(true);
                  }}
                >
                  <Eye className="h-4 w-4 text-grey-500" />
                  Make public
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton
              ariaLabel="Actions"
              variant="secondaryColor"
              size="sm"
              className="hover:bg-primary-100"
              data-cy="public-record-actions-button"
              icon={DotsHorizontal}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild data-cy="open-public-record-menu-item">
              <a
                target="_blank"
                href={`/public/site/${siteSlug}/map/plant/${material.id}`}
              >
                <ExternalLink1 className="h-4 w-4 text-grey-500" />
                View public record
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {material.accession?.taxon != null && (
              <DropdownMenuItem
                data-cy="view-qr-code-menu-item"
                onSelect={() => {
                  setQrDialogOpen(true);
                }}
              >
                <QrCode1 className="h-4 w-4" />
                View QR code
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {isPublic && (
              <DropdownMenuItem
                data-cy="make-private-menu-item"
                onClick={() => {
                  setMakePrivateDialogOpen(true);
                }}
              >
                <EyeOff className="h-4 w-4 text-grey-500" />
                Make private
              </DropdownMenuItem>
            )}
            {!isPublic && (
              <DropdownMenuItem
                data-cy="make-public-menu-item"
                onClick={() => {
                  setMakePublicDialogOpen(true);
                }}
              >
                <Eye className="h-4 w-4 text-grey-500" />
                Make public
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
          {material.accession?.taxon != null && (
            <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
              <PublicQRCodeDialogContent
                materialId={material.id}
                scientificName={material.accession.taxon.scientificName}
                accessionNumber={material.accession.accessionNumber}
                qualifier={material.qualifier}
              />
            </Dialog>
          )}
        </DropdownMenu>
      )}
      <MakePrivateDialog
        open={makePrivateDialogOpen}
        onOpenChange={setMakePrivateDialogOpen}
        id={material.id}
      />
      <MakePublicDialog
        open={makePublicDialogOpen}
        onOpenChange={setMakePublicDialogOpen}
        id={material.id}
      />
    </div>
  ) : null;
};
